import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { useMarket } from "../../context/MarketContext";
import {
  EXPIRY_BUCKET_LABEL,
  EXPIRY_BUCKET_STYLES,
  EXPIRY_BUCKETS,
  ExpiringPaymentMethodRow,
  ExpiringPaymentMethodsResponse,
  ExpiryBucket,
} from "../../types/expiringPaymentMethods";
import Pagination from "../OnboardingCustomers/components/Pagination";

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const STRIPE_CUSTOMER = "https://dashboard.stripe.com/customers/";

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function ExpiringPaymentMethods() {
  const { token } = useAuth();
  const { markets } = useMarket();

  const [data, setData] = useState<ExpiringPaymentMethodsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [carrier, setCarrier] = useState("");
  const [windowDays, setWindowDays] = useState<number>(90);
  const [includeExpired, setIncludeExpired] = useState<boolean>(false);
  const [selectedBuckets, setSelectedBuckets] = useState<ExpiryBucket[]>([]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    selectedBuckets.forEach((b) => params.append("bucket[]", b));
    if (debouncedSearch) params.set("q", debouncedSearch);
    if (carrier) params.set("carrier", carrier);
    params.set("windowDays", String(windowDays));
    params.set("includeExpired", includeExpired ? "1" : "0");
    params.set("limit", String(pageSize));
    params.set("offset", String(page * pageSize));
    try {
      const response = await request<ExpiringPaymentMethodsResponse>(
        `/api/admin/operations/payment-methods/expiring?${params.toString()}`,
        { token, markets },
      );
      setData(response);
    } catch (err) {
      setError(err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [
    token,
    markets,
    selectedBuckets,
    debouncedSearch,
    carrier,
    windowDays,
    includeExpired,
    page,
    pageSize,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const carriers = useMemo(
    () =>
      Array.from(
        new Set((data?.rows ?? []).map((r) => r.carrier).filter(Boolean) as string[]),
      ).sort(),
    [data],
  );

  const toggleBucket = (b: ExpiryBucket) => {
    setSelectedBuckets((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b],
    );
    setPage(0);
  };

  const clearFilters = () => {
    setSearch("");
    setCarrier("");
    setSelectedBuckets([]);
    setWindowDays(90);
    setIncludeExpired(false);
    setPage(0);
  };

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1600, margin: "0 auto" }}>
      <div style={{ marginBottom: "0.75rem" }}>
        <h2 style={{ margin: 0, fontSize: "1.15rem", color: "#2A3F54" }}>
          Caducidad de métodos de pago
        </h2>
        <p style={{ margin: "0.25rem 0 0", color: "#73879C", fontSize: ".85rem" }}>
          Tarjetas marcadas como default en suscripciones <strong>activas</strong>,
          ordenadas por urgencia. Las ya caducadas aparecen primero. SEPA queda
          fuera (no tiene fecha de caducidad).
        </p>
      </div>

      {/* Aggregates strip — clicking a bucket toggles the corresponding filter. */}
      {data && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            marginBottom: "0.65rem",
          }}
        >
          {EXPIRY_BUCKETS.map((b) => {
            const count = data.aggregates.countsByBucket[b] ?? 0;
            const active = selectedBuckets.includes(b);
            const palette = EXPIRY_BUCKET_STYLES[b];
            return (
              <button
                key={b}
                type="button"
                onClick={() => toggleBucket(b)}
                style={{
                  padding: "0.5rem 0.8rem",
                  borderRadius: "0.5rem",
                  border: `1px solid ${active ? palette.fg : "#dee2e6"}`,
                  background: active ? palette.bg : "white",
                  color: active ? palette.fg : "#1f2933",
                  fontSize: ".82rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                }}
              >
                <span>{EXPIRY_BUCKET_LABEL[b]}</span>
                <span
                  style={{
                    fontSize: ".72rem",
                    fontWeight: 700,
                    padding: "0.05rem 0.4rem",
                    borderRadius: "999px",
                    background: active ? "rgba(255,255,255,.25)" : "#f1f3f5",
                    color: active ? palette.fg : "#73879C",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "0.5rem",
          background: "white",
          padding: "0.7rem 0.95rem",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          marginBottom: "0.65rem",
        }}
      >
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Buscar por email, póliza, subscription ID o pm_id…"
          style={{ ...inputStyle, minWidth: 280 }}
        />
        <select
          value={carrier}
          onChange={(e) => {
            setCarrier(e.target.value);
            setPage(0);
          }}
          style={inputStyle}
        >
          <option value="">Carrier (todos)</option>
          {carriers.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <label style={labelStyle}>
          <span>Ventana</span>
          <select
            value={windowDays}
            onChange={(e) => {
              setWindowDays(parseInt(e.target.value, 10));
              setPage(0);
            }}
            style={inputStyle}
          >
            <option value={30}>30 días</option>
            <option value={60}>60 días</option>
            <option value={90}>90 días</option>
            <option value={180}>180 días</option>
            <option value={365}>365 días</option>
          </select>
        </label>
        <label
          style={{
            ...labelStyle,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => {
              setIncludeExpired(e.target.checked);
              setPage(0);
            }}
          />
          <span>Incluir ya caducadas</span>
        </label>
        <button type="button" onClick={clearFilters} style={ghostBtn}>
          Limpiar filtros
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "0.6rem 0.85rem",
            background: "#fde2e2",
            color: "#b32424",
            borderRadius: "0.5rem",
            marginBottom: "0.75rem",
            fontSize: ".85rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.55rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".82rem" }}>
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              <Th>Bucket</Th>
              <Th>Caduca</Th>
              <Th>Días</Th>
              <Th>Lead</Th>
              <Th>Póliza</Th>
              <Th>Carrier</Th>
              <Th>Mercado</Th>
              <Th>Tarjeta</Th>
              <Th>Subscription</Th>
              <Th>Stripe</Th>
            </tr>
          </thead>
          <tbody>
            {!loading && data && data.rows.length === 0 && (
              <tr>
                <td
                  colSpan={10}
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    color: "#73879C",
                  }}
                >
                  Sin resultados.
                </td>
              </tr>
            )}
            {data?.rows.map((row) => (
              <Row key={row.subscriptionUuid + row.paymentMethodId} row={row} />
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "1rem" }}>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={data?.total ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(s) => setPageSize(s)}
          disabled={loading}
        />
      </div>
    </div>
  );
}

function Row({ row }: { row: ExpiringPaymentMethodRow }) {
  const bucketKey = row.bucket as ExpiryBucket;
  const palette = EXPIRY_BUCKET_STYLES[bucketKey] ?? {
    bg: "#e9ecef",
    fg: "#495057",
  };
  const bucketLabel = EXPIRY_BUCKET_LABEL[bucketKey] ?? row.bucket;
  return (
    <tr style={{ borderTop: "1px solid #f1f3f5", verticalAlign: "top" }}>
      <Td>
        <span
          style={{
            display: "inline-block",
            padding: "0.15rem 0.5rem",
            borderRadius: "0.4rem",
            fontSize: ".7rem",
            fontWeight: 700,
            background: palette.bg,
            color: palette.fg,
            whiteSpace: "nowrap",
          }}
        >
          {bucketLabel}
        </span>
      </Td>
      <Td>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.1rem" }}>
          <strong>{fmtDate(row.expiresAt)}</strong>
          <span style={{ fontSize: ".7rem", color: "#73879C", fontFamily: "monospace" }}>
            raw: {row.expiresRaw}
          </span>
        </div>
      </Td>
      <Td>
        <span
          style={{
            fontFamily: "monospace",
            fontSize: ".82rem",
            color: row.daysToExpire < 0 ? "#b32424" : "#1f2933",
            fontWeight: row.daysToExpire < 30 ? 700 : 500,
          }}
        >
          {row.daysToExpire >= 0 ? `+${row.daysToExpire}` : row.daysToExpire}
        </span>
      </Td>
      <Td>
        {row.leadUlid ? (
          <Link
            to={`/leads/${row.leadUlid}`}
            style={{ color: "#0069d9", textDecoration: "none", fontWeight: 600 }}
          >
            {row.leadEmail ?? row.leadName ?? row.leadId ?? "—"}
          </Link>
        ) : (
          <span>{row.leadEmail ?? row.leadName ?? row.leadId ?? "—"}</span>
        )}
        {row.leadName && row.leadEmail && (
          <div style={{ fontSize: ".7rem", color: "#73879C" }}>{row.leadName}</div>
        )}
      </Td>
      <Td>
        {row.policyNumber ? (
          <span style={{ fontFamily: "monospace", fontSize: ".78rem" }}>
            {row.policyNumber}
          </span>
        ) : (
          <span style={{ color: "#adb5bd" }}>—</span>
        )}
      </Td>
      <Td>{row.carrier ?? "—"}</Td>
      <Td>
        {row.leadMarket ? (
          <span
            style={{
              fontFamily: "monospace",
              fontSize: ".7rem",
              background: "#e9ecef",
              color: "#495057",
              padding: "0.1rem 0.4rem",
              borderRadius: "0.35rem",
            }}
          >
            {row.leadMarket}
          </span>
        ) : (
          <span style={{ color: "#adb5bd" }}>—</span>
        )}
      </Td>
      <Td>
        <span style={{ fontFamily: "monospace", fontSize: ".78rem" }}>
          {row.lastNumbers ?? row.paymentMethodId}
        </span>
      </Td>
      <Td>
        <Link
          to={`/subscriptions/${row.subscriptionUuid}`}
          style={{
            color: "#0069d9",
            textDecoration: "none",
            fontFamily: "monospace",
            fontSize: ".72rem",
          }}
        >
          {row.subscriptionUuid.slice(0, 8)}…
        </Link>
      </Td>
      <Td>
        {row.stripeCustomerId ? (
          <a
            href={`${STRIPE_CUSTOMER}${row.stripeCustomerId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0069d9", textDecoration: "none", fontSize: ".75rem" }}
            title={row.stripeCustomerId}
          >
            Stripe ↗
          </a>
        ) : (
          <span style={{ color: "#adb5bd" }}>—</span>
        )}
      </Td>
    </tr>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "0.55rem 0.7rem",
        textAlign: "left",
        fontWeight: 600,
        color: "#1f2933",
        fontSize: ".7rem",
        textTransform: "uppercase",
        letterSpacing: ".3px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td style={{ padding: "0.55rem 0.7rem" }}>{children}</td>;
}

const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".85rem",
};

const labelStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.3rem",
  fontSize: ".78rem",
  color: "#73879C",
  fontWeight: 600,
};

const ghostBtn: React.CSSProperties = {
  padding: "0.4rem 0.85rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".78rem",
  fontWeight: 600,
  cursor: "pointer",
};
